import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { Sync, Download } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { evitalrxReturnsAPI, lookupAPI } from '../services/api';
import { Store } from '../types';

interface PurchaseReturn {
  id: number;
  store_id: number;
  store_name: string | null;
  evitalrx_id: string;
  sr_no: string | null;
  bill_date: string | null;
  entry_date: string | null;
  entry_by: string | null;
  distributor_name: string | null;
  due_amount: string;
  amount: string;
  status: 'Due' | 'Settled';
}

interface Props {
  selectedStore: Store | null;
}

const inr = (n: string | number | null) =>
  n == null || n === '' || Number(n) === 0
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function fmtDate(val: string | null) {
  if (!val) return '—';
  return dayjs(val).format('DD-MMM-YY');
}

function statusChip(status: string) {
  switch (status) {
    case 'Settled':
      return <Chip size="small" color="success" label="Settled" />;
    case 'Due':
      return <Chip size="small" color="warning" label="Due" />;
    default:
      return <Chip size="small" label={status} />;
  }
}

const EvitalrxPurchaseReturns: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const storeOptions = [
    { value: 'all' as const, label: 'All Stores' },
    ...stores.map(s => ({ value: s.id, label: s.store_name })),
  ];

  // Sync controls
  const [syncStore, setSyncStore] = useState<number | 'all'>(selectedStore ? selectedStore.id : 'all');
  const [syncFrom, setSyncFrom]   = useState<Dayjs | null>(dayjs());
  const [syncTo, setSyncTo]       = useState<Dayjs | null>(dayjs());
  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; skipped: number } | null>(null);
  const [syncError, setSyncError]   = useState('');

  // List filters
  const [listStore, setListStore]         = useState<number | 'all'>(selectedStore ? selectedStore.id : 'all');
  const [listFrom, setListFrom]           = useState<Dayjs | null>(null);
  const [listTo, setListTo]               = useState<Dayjs | null>(null);
  const [distFilter, setDistFilter]       = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [returns, setReturns]             = useState<PurchaseReturn[]>([]);
  const [loading, setLoading]             = useState(false);

  const loadReturns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await evitalrxReturnsAPI.getReturns({
        store_id: listStore,
        from_date: listFrom ? listFrom.format('YYYY-MM-DD') : undefined,
        to_date: listTo ? listTo.format('YYYY-MM-DD') : undefined,
        distributor_name: distFilter || undefined,
        status: statusFilter || undefined,
      });
      setReturns(data);
    } catch {
      setReturns([]);
    } finally {
      setLoading(false);
    }
  }, [listStore, listFrom, listTo, distFilter, statusFilter]);

  useEffect(() => { loadReturns(); }, [loadReturns]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError('');
    try {
      const res = await evitalrxReturnsAPI.sync(
        syncStore,
        syncFrom?.format('YYYY-MM-DD'),
        syncTo?.format('YYYY-MM-DD'),
      );
      setSyncResult({ synced: res.synced, skipped: res.skipped });
      loadReturns();
    } catch (e: any) {
      setSyncError(e?.response?.data?.message || 'Sync failed. Check scraper logs.');
    } finally {
      setSyncing(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Sr No', 'Bill Date', 'Entry By', 'Distributor', 'Amount', 'Due Amount', 'Status'];
    const rows = returns.map(r => [
      r.sr_no || '',
      fmtDate(r.bill_date),
      r.entry_by || '',
      r.distributor_name || '',
      r.amount,
      r.due_amount,
      r.status,
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase_returns_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalAmount = returns.reduce((s, r) => s + Number(r.amount), 0);
  const totalDue    = returns.reduce((s, r) => s + Number(r.due_amount), 0);
  const showStore   = listStore === 'all';

  return (
    <Box sx={{ p: 2 }}>
      {/* Sync bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Sync from evitalrx:</Typography>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Store</InputLabel>
          <Select value={syncStore} label="Store" onChange={e => setSyncStore(e.target.value as number | 'all')}>
            {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <DatePicker label="From" value={syncFrom} onChange={setSyncFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <DatePicker label="To" value={syncTo} onChange={setSyncTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />

        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Now'}
        </Button>

        {syncResult && (
          <Alert severity="success" sx={{ py: 0 }}>
            Synced {syncResult.synced} new returns, {syncResult.skipped} already exist
          </Alert>
        )}
        {syncError && <Alert severity="error" sx={{ py: 0 }}>{syncError}</Alert>}
      </Paper>

      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Store</InputLabel>
          <Select value={listStore} label="Store" onChange={e => setListStore(e.target.value as number | 'all')}>
            {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <DatePicker label="From" value={listFrom} onChange={setListFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <DatePicker label="To" value={listTo} onChange={setListTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />

        <TextField size="small" label="Distributor" value={distFilter}
          onChange={e => setDistFilter(e.target.value)} sx={{ width: 180 }} />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Due">Due</MenuItem>
            <MenuItem value="Settled">Settled</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" size="small" onClick={loadReturns} disabled={loading}>Search</Button>
        <Button size="small" startIcon={<Download />} onClick={exportCSV} disabled={returns.length === 0}>CSV</Button>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                {showStore && <TableCell><strong>Store</strong></TableCell>}
                <TableCell><strong>Sr No</strong></TableCell>
                <TableCell><strong>Bill Date</strong></TableCell>
                <TableCell><strong>Entry By</strong></TableCell>
                <TableCell><strong>Distributor</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell align="right"><strong>Due Amount</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showStore ? 8 : 7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No purchase returns found. Run a sync first.
                  </TableCell>
                </TableRow>
              ) : returns.map(r => (
                <TableRow key={r.id} hover>
                  {showStore && (
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {r.store_name || '—'}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.sr_no}</TableCell>
                  <TableCell>{fmtDate(r.bill_date)}</TableCell>
                  <TableCell>{r.entry_by || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.distributor_name || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(r.amount)}</TableCell>
                  <TableCell align="right" sx={{ color: Number(r.due_amount) > 0 ? 'warning.main' : 'inherit' }}>
                    {inr(r.due_amount)}
                  </TableCell>
                  <TableCell align="center">{statusChip(r.status)}</TableCell>
                </TableRow>
              ))}

              {returns.length > 0 && (
                <TableRow sx={{ bgcolor: '#fff3e0' }}>
                  {showStore && <TableCell />}
                  <TableCell colSpan={4} sx={{ fontWeight: 700 }}>TOTAL ({returns.length} returns)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(totalAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: totalDue > 0 ? 'warning.main' : 'inherit' }}>
                    {inr(totalDue)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {returns.length === 1000 && (
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Showing first 1000 results — use filters to narrow down
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default EvitalrxPurchaseReturns;
